import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { isPostOrEventOrImageSpace } from "@circle-react/helpers/spaceHelpers";
import { postQueries } from "@circle-react/lib/queries/posts";
import { flatten, noop, uniqBy } from "lodash";
import { useCallback, useEffect, useEffectEvent, useMemo } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from "react-query";

import { cleanEmptyProperties } from "@/react/helpers/cleanEmptyProperties";
import { useInfiniteQueryHelpers } from "@/react/hooks/reactQuery/useInfiniteQueryHelpers";
import type { Event } from "@/react/types/Event";
import type { Status as EventStatus } from "@/react/types/Event";
import type { PaginationHelper } from "@/react/types/InternalAPIHelper";
import type { Post } from "@/react/types/Post";

import { useCalendarViewInfiniteFetch } from "./useCalendarViewInfiniteFetch";

type PostListLayoutTypes =
  | "calendar"
  | "pinned_post_sidebar"
  | "posts"
  | "cards"
  | "list"
  | "thumbnail"
  | "feed"
  | "masonry"
  | "grid";

export type EventStatusFilter = EventStatus | "all";

export interface PostListParams {
  filter_date?: {
    start_date?: string;
    end_date?: string;
  };
  include_top_pinned_post?: boolean;
  only_pinned?: boolean;
  past_events?: boolean;
  per_page?: number;
  sort?: any;
  status?: EventStatusFilter;
  topics?: string[];
  used_on?: PostListLayoutTypes;
}

export interface OnPostChangeOptions {
  refetch?: boolean;
}

const normalizePostListParams = (params: PostListParams) => {
  if (!params.filter_date) return params;
  return { ...params, filter_date: cleanEmptyProperties(params.filter_date) };
};

type SpaceRecord = Event | Post;

export const usePostList = <TRecord extends SpaceRecord = Post>(
  params: PostListParams = {
    per_page: 12, // 12 is multiple of 4, 3, 2 and 1 hence we always will have full rows
  },
  isEnabled: boolean = true,
  onSuccess: any = noop,
) => {
  const queryClient = useQueryClient();
  const { removeRecord, findAndReplaceRecord } = useInfiniteQueryHelpers();
  const currentSpaceContext = useCurrentSpaceContext();
  const { data: space } = currentSpaceContext;
  const normalizedParams = normalizePostListParams(params);
  const query = postQueries.spacePosts({
    spaceId: space?.id,
    params: normalizedParams,
  });
  const queryKey = query.queryKey;

  const isCalendarView = normalizedParams.used_on === "calendar";
  const isQueryEnabled =
    isEnabled && !!space?.id && isPostOrEventOrImageSpace(space);

  const {
    data,
    hasNextPage,
    isInitialLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...query,
    placeholderData: isCalendarView ? keepPreviousData : undefined,
    enabled: isQueryEnabled,
  });

  const notifySuccess = useEffectEvent((pageData: typeof data) => {
    onSuccess(pageData);
  });

  useEffect(() => {
    if (!data) return;
    notifySuccess(data);
  }, [data]);

  const onPostChange = useCallback(
    async (post: any, { refetch = true }: OnPostChangeOptions = {}) => {
      let fetchedPost = post;
      if (refetch) {
        try {
          fetchedPost = await queryClient.fetchQuery(
            postQueries.postDetails({
              spaceId: post.space_id,
              postSlug: post.slug,
            }),
          );
        } catch (err) {
          console.error(err);
        } finally {
          findAndReplaceRecord(fetchedPost, queryKey);
        }
      } else {
        findAndReplaceRecord(fetchedPost, queryKey);
      }
    },
    [findAndReplaceRecord, queryClient, queryKey],
  );

  const onPostDestroy = useCallback(
    (postId: number) => {
      removeRecord(postId, queryKey);
    },
    [removeRecord, queryKey],
  );

  const posts = useMemo(
    () =>
      isInitialLoading || !data
        ? []
        : uniqBy(
            flatten(data.pages.map(page => page.records as TRecord[])),
            "id",
          ),
    [data, isInitialLoading],
  );
  const mapPages = <TPageResult>(
    callback: (page: PaginationHelper<TRecord>) => TPageResult,
  ) => {
    if (isInitialLoading || !data) return [];
    return data.pages.map(page => callback(page as PaginationHelper<TRecord>));
  };

  const lastPage = data?.pages[data?.pages.length - 1];
  useCalendarViewInfiniteFetch({ lastPage, isCalendarView, fetchNextPage });

  return {
    mapPages,
    hasNextPage,
    refetch,
    isLoading: isInitialLoading,
    isError,
    error,
    fetchNextPage,
    isFetchingNextPage,
    pages: data?.pages,
    posts,
    onPostChange,
    onPostDestroy,
  };
};

export const usePostListPinnedOnSidebar = (postsPerPage?: number) =>
  usePostList({
    only_pinned: true,
    per_page: postsPerPage || 50,
    used_on: "pinned_post_sidebar",
  });
